import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Alert, CircularProgress, Avatar, Tooltip, IconButton,
  LinearProgress, Checkbox, FormControlLabel, Divider, List,
  ListItem, ListItemText, ListItemIcon, Collapse, InputAdornment,
} from '@mui/material';
import {
  ExitToApp, Add, Edit, Search, CheckCircle, RadioButtonUnchecked,
  Warning, TimerOff, ExpandMore, ExpandLess, AddTask,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_CFG: Record<string, { label: string; color: string; bg: string }> = {
  active:       { label: 'Serving Notice', color: '#1565c0', bg: '#e3f2fd' },
  extended:     { label: 'Extended',       color: '#6a1b9a', bg: '#f3e5f5' },
  waived:       { label: 'Waived',         color: '#e65100', bg: '#fff3e0' },
  garden_leave: { label: 'Garden Leave',   color: '#2e7d32', bg: '#e8f5e9' },
  completed:    { label: 'Completed',      color: '#37474f', bg: '#eceff1' },
  cancelled:    { label: 'Cancelled',      color: '#78909c', bg: '#f5f5f5' },
};

const REASONS: Record<string, string> = {
  better_opportunity: 'Better Opportunity', compensation: 'Compensation',
  career_growth: 'Career Growth', work_life_balance: 'Work-Life Balance',
  relocation: 'Relocation', personal_reasons: 'Personal Reasons',
  family: 'Family', health: 'Health', retirement: 'Retirement',
  redundancy: 'Redundancy', dismissal: 'Dismissal',
  contract_end: 'Contract End', other: 'Other',
};

const CATEGORY_COLOR: Record<string, string> = {
  IT: '#1565c0', Facilities: '#4caf50', Manager: '#e65100',
  HR: '#8e24aa', Finance: '#2e7d32',
};

interface ClearanceItem {
  id: string; title: string; description?: string; category?: string;
  isCompleted: boolean; completedDate?: string; completedByName?: string; sortOrder: number;
}

interface NP {
  id: string; employeeId: string; resignationDate: string; lastWorkingDay: string;
  actualLastDay?: string; noticeDays: number; resignationReason?: string;
  status: string; hrNotes?: string; handoverNotes?: string;
  exitInterviewDone: boolean; assetsReturned: boolean; accessRevoked: boolean;
  finalPayProcessed: boolean; referenceProvided: boolean;
  daysServed: number; daysRemaining: number; progressPct: number;
  clearanceItems?: ClearanceItem[];
  employee?: { firstName: string; lastName: string; department?: { name: string }; position?: { title: string }; email?: string };
}

interface Stats { active: number; completed: number; upcoming7: number; overdue: number }

const emptyForm = {
  employeeId: '', resignationDate: '', lastWorkingDay: '', noticeDays: 30,
  resignationReason: 'better_opportunity', hrNotes: '', handoverNotes: '',
};

const STATUS_TRANSITIONS: Record<string, string[]> = {
  active: ['extended', 'waived', 'garden_leave', 'completed', 'cancelled'],
  extended: ['completed', 'cancelled'],
  waived: ['completed'],
  garden_leave: ['completed', 'cancelled'],
  completed: [],
  cancelled: [],
};

export default function NoticePeriod() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes((user as any)?.role || '');
  const isManager = (user as any)?.role === 'manager';

  const [tab, setTab] = useState(0);
  const [list, setList] = useState<NP[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selected, setSelected] = useState<NP | null>(null);
  const [expandChecklist, setExpandChecklist] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [editId, setEditId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [statusDialog, setStatusDialog] = useState<{ id: string; current: string } | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [actualLastDay, setActualLastDay] = useState('');

  const [addItemDialog, setAddItemDialog] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState('');
  const [newItemCategory, setNewItemCategory] = useState('HR');

  const STATUS_TABS = ['Active', 'Garden Leave', 'Completed', 'All'];
  const statusForTab: Record<number, string | undefined> = {
    0: 'active', 1: 'garden_leave', 2: 'completed', 3: undefined,
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusForTab[tab]) params.set('status', statusForTab[tab]!);
      if (search) params.set('search', search);

      const calls: Promise<any>[] = [
        apiFetch(`/api/notice-periods?${params}`),
        apiFetch('/api/notice-periods/stats'),
        ...(isHR ? [apiFetch('/api/employees?limit=300')] : []),
      ];

      const results = await Promise.allSettled(calls);
      if (results[0].status === 'fulfilled') setList(results[0].value ?? []);
      if (results[1].status === 'fulfilled') setStats(results[1].value);
      if (results[2] && results[2].status === 'fulfilled') {
        const d = (results[2] as any).value;
        setEmployees(Array.isArray(d) ? d : (d.data ?? []));
      }
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [tab, search, isHR]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setFormData({ ...emptyForm }); setFormOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editId) {
        await apiFetch(`/api/notice-periods/${editId}`, { method: 'PATCH', body: JSON.stringify(formData) });
      } else {
        await apiFetch('/api/notice-periods', { method: 'POST', body: JSON.stringify(formData) });
      }
      setFormOpen(false);
      load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const changeStatus = async () => {
    if (!statusDialog) return;
    setSaving(true);
    try {
      await apiFetch(`/api/notice-periods/${statusDialog.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus, hrNotes: statusNote, actualLastDay }),
      });
      setStatusDialog(null);
      if (selected?.id === statusDialog.id) {
        const fresh = await apiFetch(`/api/notice-periods/${statusDialog.id}`);
        setSelected(fresh);
      }
      load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const toggleItem = async (npId: string, itemId: string) => {
    await apiFetch(`/api/notice-periods/${npId}/clearance/${itemId}/toggle`, { method: 'PATCH' });
    const fresh = await apiFetch(`/api/notice-periods/${npId}`);
    setSelected(fresh);
  };

  const addItem = async () => {
    if (!selected || !newItemTitle) return;
    await apiFetch(`/api/notice-periods/${selected.id}/clearance`, {
      method: 'POST',
      body: JSON.stringify({ title: newItemTitle, category: newItemCategory }),
    });
    setAddItemDialog(false);
    setNewItemTitle('');
    const fresh = await apiFetch(`/api/notice-periods/${selected.id}`);
    setSelected(fresh);
  };

  const selectRow = async (np: NP) => {
    const full = await apiFetch(`/api/notice-periods/${np.id}`);
    setSelected(full);
  };

  const completedCount = (items: ClearanceItem[]) => items.filter(i => i.isCompleted).length;
  const groupByCategory = (items: ClearanceItem[]) => {
    const groups: Record<string, ClearanceItem[]> = {};
    items.forEach(i => {
      const cat = i.category || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(i);
    });
    return groups;
  };

  return (
    <Box sx={{ p: 3, display: 'flex', gap: 2, minHeight: '80vh' }}>
      {/* Left panel */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: '#b71c1c', width: 44, height: 44 }}><ExitToApp /></Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700}>Notice Period Tracker</Typography>
              <Typography variant="body2" color="text.secondary">Manage resignations, clearance & handover</Typography>
            </Box>
          </Box>
          {isHR && (
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}
              sx={{ bgcolor: '#b71c1c', '&:hover': { bgcolor: '#7f0000' } }}>
              Record Resignation
            </Button>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Stats */}
        {stats && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              { label: 'Serving Notice', value: stats.active,    color: '#1565c0' },
              { label: 'Ending ≤7 Days', value: stats.upcoming7, color: '#e65100' },
              { label: 'Overdue Exit',   value: stats.overdue,   color: '#b71c1c' },
              { label: 'Completed',      value: stats.completed, color: '#2e7d32' },
            ].map(s => (
              <Grid size={{ xs: 6, sm: 3 }} key={s.label}>
                <Paper sx={{ p: 1.5, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
                  <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        {stats && stats.overdue > 0 && (
          <Alert severity="error" icon={<TimerOff />} sx={{ mb: 2 }}>
            <strong>{stats.overdue} employee{stats.overdue !== 1 ? 's' : ''}</strong> past their last working day — please confirm completion or extend.
          </Alert>
        )}

        <Paper sx={{ p: 1.5, mb: 1.5, display: 'flex', gap: 2 }}>
          <TextField size="small" placeholder="Search employee…" value={search}
            onChange={e => setSearch(e.target.value)} sx={{ minWidth: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        </Paper>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1.5 }}>
          {STATUS_TABS.map((t, i) => <Tab key={i} label={t} />)}
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Resign Date</TableCell>
                  <TableCell>Last Day</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Clearance</TableCell>
                  {isHR && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {list.length === 0 && (
                  <TableRow><TableCell colSpan={isHR ? 7 : 6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No records found
                  </TableCell></TableRow>
                )}
                {list.map(np => {
                  const isOverdue = np.status === 'active' && np.daysRemaining === 0 && np.progressPct < 100;
                  return (
                    <TableRow key={np.id} hover selected={selected?.id === np.id}
                      onClick={() => selectRow(np)} sx={{ cursor: 'pointer',
                        bgcolor: isOverdue ? '#ffebee40' : undefined }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {np.employee?.firstName} {np.employee?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{np.employee?.department?.name}</Typography>
                      </TableCell>
                      <TableCell><Typography variant="body2">{np.resignationDate}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={isOverdue ? 700 : 400}
                          color={isOverdue ? 'error.main' : undefined}>
                          {np.lastWorkingDay}
                          {isOverdue && ' ⚠️'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress variant="determinate" value={np.progressPct}
                            sx={{ flex: 1, height: 6, borderRadius: 3,
                              '& .MuiLinearProgress-bar': { bgcolor: np.progressPct === 100 ? '#2e7d32' : '#1565c0' } }} />
                          <Typography variant="caption">{np.progressPct}%</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {np.daysRemaining > 0 ? `${np.daysRemaining}d left` : `${np.daysServed}d served`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={STATUS_CFG[np.status]?.label ?? np.status}
                          sx={{ bgcolor: STATUS_CFG[np.status]?.bg, color: STATUS_CFG[np.status]?.color,
                            fontWeight: 600, fontSize: '0.65rem' }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {[
                            np.exitInterviewDone && '✓ Exit Intv',
                            np.assetsReturned && '✓ Assets',
                            np.accessRevoked && '✓ Access',
                          ].filter(Boolean).join(' · ') || '—'}
                        </Typography>
                      </TableCell>
                      {isHR && (
                        <TableCell>
                          {STATUS_TRANSITIONS[np.status]?.length > 0 && (
                            <Button size="small" variant="outlined"
                              onClick={e => { e.stopPropagation(); setStatusDialog({ id: np.id, current: np.status }); setNewStatus(STATUS_TRANSITIONS[np.status][0]); setStatusNote(''); setActualLastDay(''); }}>
                              Update
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Detail panel */}
      {selected && (
        <Paper sx={{ width: 380, p: 2, flexShrink: 0, overflowY: 'auto', maxHeight: '90vh', position: 'sticky', top: 24 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              {selected.employee?.firstName} {selected.employee?.lastName}
            </Typography>
            <IconButton size="small" onClick={() => setSelected(null)}>✕</IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {selected.employee?.position?.title} · {selected.employee?.department?.name}
          </Typography>

          <Chip size="small" label={STATUS_CFG[selected.status]?.label ?? selected.status}
            sx={{ bgcolor: STATUS_CFG[selected.status]?.bg, color: STATUS_CFG[selected.status]?.color, mb: 1 }} />

          <Box sx={{ my: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Notice progress</Typography>
              <Typography variant="caption" fontWeight={600}>{selected.progressPct}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={selected.progressPct}
              sx={{ height: 8, borderRadius: 4,
                '& .MuiLinearProgress-bar': { bgcolor: selected.progressPct === 100 ? '#2e7d32' : '#1565c0' } }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">{selected.resignationDate}</Typography>
              <Typography variant="caption" color="text.secondary">{selected.lastWorkingDay}</Typography>
            </Box>
          </Box>

          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            {[
              { label: 'Days Served', value: selected.daysServed },
              { label: 'Days Left', value: selected.daysRemaining },
              { label: 'Notice Period', value: `${selected.noticeDays}d` },
              { label: 'Reason', value: REASONS[selected.resignationReason ?? ''] ?? '—' },
            ].map(s => (
              <Grid size={{ xs: 6 }} key={s.label}>
                <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, p: 1, textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight={700}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 1.5 }} />

          {/* Quick flags */}
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Quick Flags</Typography>
          <Grid container spacing={0.5} sx={{ mb: 1.5 }}>
            {[
              { key: 'exitInterviewDone', label: 'Exit Interview' },
              { key: 'assetsReturned',    label: 'Assets Returned' },
              { key: 'accessRevoked',     label: 'Access Revoked' },
              { key: 'finalPayProcessed', label: 'Final Pay' },
              { key: 'referenceProvided', label: 'Reference' },
            ].map(f => (
              <Grid size={{ xs: 6 }} key={f.key}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {(selected as any)[f.key]
                    ? <CheckCircle fontSize="small" color="success" />
                    : <RadioButtonUnchecked fontSize="small" sx={{ color: '#bbb' }} />}
                  <Typography variant="caption">{f.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 1.5 }} />

          {/* Clearance checklist */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Clearance Checklist ({completedCount(selected.clearanceItems ?? [])}/{selected.clearanceItems?.length})
            </Typography>
            <Box>
              {isHR && (
                <Tooltip title="Add item">
                  <IconButton size="small" onClick={() => setAddItemDialog(true)}><AddTask fontSize="small" /></IconButton>
                </Tooltip>
              )}
              <IconButton size="small" onClick={() => setExpandChecklist(p => !p)}>
                {expandChecklist ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            </Box>
          </Box>

          {selected.clearanceItems && selected.clearanceItems.length > 0 && (
            <LinearProgress variant="determinate"
              value={Math.round((completedCount(selected.clearanceItems) / selected.clearanceItems.length) * 100)}
              sx={{ mt: 0.5, mb: 1, height: 4, borderRadius: 2,
                '& .MuiLinearProgress-bar': { bgcolor: completedCount(selected.clearanceItems) === selected.clearanceItems.length ? '#2e7d32' : '#1565c0' } }} />
          )}

          <Collapse in={expandChecklist}>
            {Object.entries(groupByCategory(selected.clearanceItems ?? [])).map(([cat, items]) => (
              <Box key={cat} sx={{ mb: 1 }}>
                <Typography variant="caption" fontWeight={700}
                  sx={{ color: CATEGORY_COLOR[cat] ?? '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {cat}
                </Typography>
                {items.map(item => (
                  <Box key={item.id} sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.25,
                    opacity: item.isCompleted ? 0.6 : 1,
                  }}>
                    <Checkbox size="small" checked={item.isCompleted}
                      onChange={() => toggleItem(selected.id, item.id)}
                      disabled={!isHR && !isManager}
                      sx={{ p: 0, mt: 0.25 }} />
                    <Box>
                      <Typography variant="body2"
                        sx={{ textDecoration: item.isCompleted ? 'line-through' : 'none' }}>
                        {item.title}
                      </Typography>
                      {item.isCompleted && item.completedByName && (
                        <Typography variant="caption" color="text.secondary">
                          ✓ {item.completedByName} · {item.completedDate}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
          </Collapse>

          {selected.hrNotes && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>HR Notes</Typography>
              <Typography variant="body2" color="text.secondary">{selected.hrNotes}</Typography>
            </>
          )}

          {selected.handoverNotes && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Handover Notes</Typography>
              <Typography variant="body2" color="text.secondary">{selected.handoverNotes}</Typography>
            </>
          )}

          {isHR && STATUS_TRANSITIONS[selected.status]?.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Button fullWidth variant="outlined" color="error"
                onClick={() => { setStatusDialog({ id: selected.id, current: selected.status }); setNewStatus(STATUS_TRANSITIONS[selected.status][0]); setStatusNote(''); setActualLastDay(''); }}>
                Update Status
              </Button>
            </>
          )}
        </Paper>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Resignation</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField select fullWidth label="Employee" value={formData.employeeId}
            onChange={e => setFormData(p => ({ ...p, employeeId: e.target.value }))} required>
            {employees.map(e => <MenuItem key={e.id} value={e.id}>{e.firstName} {e.lastName} — {e.department?.name}</MenuItem>)}
          </TextField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Resignation Date" type="date" InputLabelProps={{ shrink: true }}
              value={formData.resignationDate} onChange={e => setFormData(p => ({ ...p, resignationDate: e.target.value }))} required />
            <TextField fullWidth label="Last Working Day" type="date" InputLabelProps={{ shrink: true }}
              value={formData.lastWorkingDay} onChange={e => setFormData(p => ({ ...p, lastWorkingDay: e.target.value }))} required />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Notice Period (days)" type="number" value={formData.noticeDays}
              onChange={e => setFormData(p => ({ ...p, noticeDays: parseInt(e.target.value) }))} />
            <TextField select fullWidth label="Reason" value={formData.resignationReason}
              onChange={e => setFormData(p => ({ ...p, resignationReason: e.target.value }))}>
              {Object.entries(REASONS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </TextField>
          </Box>
          <TextField fullWidth label="HR Notes" multiline rows={2} value={formData.hrNotes}
            onChange={e => setFormData(p => ({ ...p, hrNotes: e.target.value }))} />
          <TextField fullWidth label="Handover Notes" multiline rows={2} value={formData.handoverNotes}
            onChange={e => setFormData(p => ({ ...p, handoverNotes: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}
            disabled={saving || !formData.employeeId || !formData.resignationDate || !formData.lastWorkingDay}
            sx={{ bgcolor: '#b71c1c' }}>
            {saving ? 'Saving…' : 'Record'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status change dialog */}
      <Dialog open={!!statusDialog} onClose={() => setStatusDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Update Status</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField select fullWidth label="New Status" value={newStatus}
            onChange={e => setNewStatus(e.target.value)}>
            {statusDialog && STATUS_TRANSITIONS[statusDialog.current]?.map(s => (
              <MenuItem key={s} value={s}>{STATUS_CFG[s]?.label ?? s}</MenuItem>
            ))}
          </TextField>
          {newStatus === 'completed' && (
            <TextField fullWidth label="Actual Last Day" type="date" InputLabelProps={{ shrink: true }}
              value={actualLastDay} onChange={e => setActualLastDay(e.target.value)} />
          )}
          <TextField fullWidth label="Notes" multiline rows={2} value={statusNote}
            onChange={e => setStatusNote(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={changeStatus} disabled={saving || !newStatus}
            sx={{ bgcolor: '#b71c1c' }}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Add clearance item dialog */}
      <Dialog open={addItemDialog} onClose={() => setAddItemDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Clearance Item</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField fullWidth label="Item Title" value={newItemTitle}
            onChange={e => setNewItemTitle(e.target.value)} />
          <TextField select fullWidth label="Category" value={newItemCategory}
            onChange={e => setNewItemCategory(e.target.value)}>
            {['IT', 'HR', 'Finance', 'Manager', 'Facilities', 'Other'].map(c => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddItemDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={addItem} disabled={!newItemTitle}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
